'use client';

import type { MqttClient, PacketCallback } from 'mqtt';
import type { PropsWithChildren } from 'react';
import { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useUnmount } from 'react-use';
import { useGetMqttConnectionDetails } from '../generated/mqtt-rest-api/mqtt-rest-api';
import {
  addTopicSubscriber,
  getTopics,
  notifyTopicSubscribers,
  removeTopicSubscriber,
  type TopicMessageCallback,
  type TopicSubscribersMap,
} from './topicSubscriptions';

export type { MqttClient, PacketCallback };

export type MqttContextType = {
  client: MqttClient | null;
  // connect mqtt client if not connected
  connectClient: () => Promise<void>;
  // disconnect mqtt client
  disconnectClient: () => void;
  // publish a new message to a topic
  publish: MqttClient['publish'];
  // subscribe to a topic and listen for messages
  subscribeToTopic: (
    topic: string | string[],
    callback: (message: string | Buffer) => void,
  ) => void;
  // unsubscribe from a topic
  unsubscribeFromTopic: (
    topic: string | string[],
    callback?: (message: string | Buffer) => void,
  ) => void;
};

export const MqttContext = createContext<MqttContextType | null>(null);

// cap auto-reconnect so a flapping network can't spin forever. this is the safety net for
// *transient* failures; a rejected CONNACK (bad creds) is fatal and bails out on the first error.
const MAX_MQTT_RECONNECT_ATTEMPTS = 10;
// space out retries so we don't hammer the broker (mqtt.js default is an aggressive 1000ms).
const MQTT_RECONNECT_PERIOD_MS = 5000;

export type MqttProviderProps = PropsWithChildren<{
  resourceType: string;
  isDisabled?: boolean;
  username?: string;
}>;
export function MqttProvider({
  children,
  resourceType,
  isDisabled = false,
  username = '',
}: MqttProviderProps) {
  const [client, setClient] = useState<MqttClient | null>(null);
  const clientRef = useRef<MqttClient | null>(null);
  const pendingPublishRef = useRef<Parameters<MqttClient['publish']>[]>([]);
  const topicSubscribersRef = useRef<TopicSubscribersMap>(new Map());

  const { data: connectionData } = useGetMqttConnectionDetails(
    { resourceTypes: [resourceType] },
    { query: { staleTime: Infinity, enabled: !isDisabled } },
  );
  const brokerUrl = connectionData?.[0]?.brokerUrl;
  const jwtToken = connectionData?.[0]?.jwtToken;
  const isConnectingRef = useRef(false);
  const reconnectAttemptsRef = useRef(0);
  // latch set once we permanently give up (fatal auth error or exhausted retries). blocks new
  // client creation so publish/subscribe can't keep spawning fresh clients that fail all over again.
  const hasGivenUpRef = useRef(false);
  const setActiveClient = useCallback((nextClient: MqttClient | null) => {
    clientRef.current = nextClient;
    setClient(nextClient);
  }, []);
  const handleMessage = useCallback((topic: string, message: Buffer) => {
    notifyTopicSubscribers(topicSubscribersRef.current, topic, message);
  }, []);
  const connectClientIfRequired = useCallback(async () => {
    if (!brokerUrl || brokerUrl.startsWith('wss://null')) return; // because backend sometimes return url like 'wss://null/mqtt'
    // once we've permanently given up, don't recreate a client until the connection params change
    // (see the reset effect below). otherwise every publish/subscribe respawns a doomed client.
    if (hasGivenUpRef.current) {
      return;
    }
    if (clientRef.current?.connected) {
      return;
    }
    if (isConnectingRef.current) {
      return;
    }
    // tear down any orphan (non-connected, still auto-retrying) client before making a new one
    // so we never end up with multiple clients racing to reconnect.
    if (clientRef.current) {
      clientRef.current.removeAllListeners();
      clientRef.current.end(true);
      clientRef.current = null;
    }
    isConnectingRef.current = true;
    reconnectAttemptsRef.current = 0;
    try {
      const mqtt = (
        await import(
          /* webpackChunkName: "lib-mqtt" */
          'mqtt'
        )
      ).default;
      const newClient = mqtt.connect(brokerUrl, {
        username,
        password: jwtToken,
        reconnectPeriod: MQTT_RECONNECT_PERIOD_MS,
      });
      // permanently stop this client: drop listeners (so 'close'/'reconnect' can't fire again),
      // force-close (skips graceful DISCONNECT and cancels retries), and latch the give-up flag.
      const giveUp = (reason: string) => {
        if (hasGivenUpRef.current) return;
        hasGivenUpRef.current = true;
        console.error(`mqtt client: giving up - ${reason}`);
        newClient.removeAllListeners();
        newClient.end(true);
        if (clientRef.current === newClient) {
          setActiveClient(null);
        }
        isConnectingRef.current = false;
      };
      newClient.on('connect', () => {
        console.debug('mqtt client: connected');
        reconnectAttemptsRef.current = 0;
        pendingPublishRef.current.splice(0).forEach((publishArgs) => {
          newClient.publish(...publishArgs);
        });
        const subscribedTopics = Array.from(topicSubscribersRef.current.keys());
        if (subscribedTopics.length) {
          newClient.subscribe(subscribedTopics, (error) => {
            if (error) {
              console.debug('mqtt client: Error re-subscribing to MQTT topics', error);
            }
          });
        }
      });
      newClient.on('message', handleMessage);
      newClient.on('error', (error) => {
        console.error('mqtt client: error:', error);
      });
      // mqtt.js auto-reconnects on every close; count the attempts as a safety net for transient
      // failures and bail out once the ceiling is hit so we can't loop forever.
      newClient.on('reconnect', () => {
        reconnectAttemptsRef.current += 1;
        if (reconnectAttemptsRef.current > MAX_MQTT_RECONNECT_ATTEMPTS) {
          giveUp(`${MAX_MQTT_RECONNECT_ATTEMPTS} failed reconnect attempts`);
        }
      });
      newClient.on('close', () => {
        isConnectingRef.current = false;
      });
      setActiveClient(newClient);
    } catch (error) {
      console.error('mqtt client: Error connecting to mqtt broker', error);
      isConnectingRef.current = false;
    }
  }, [brokerUrl, handleMessage, jwtToken, setActiveClient, username]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: fresh connection params (new token after re-login, different broker/user) clear the give-up latch so a previously-rejected session can connect again.
  useEffect(() => {
    hasGivenUpRef.current = false;
    reconnectAttemptsRef.current = 0;
  }, [brokerUrl, jwtToken, username]);
  const disconnectClient = useCallback(() => {
    const activeClient = clientRef.current;
    // only call end() on a connected client to avoid "Close received after close" errors
    if (activeClient?.connected) {
      activeClient.end();
    }
    isConnectingRef.current = false;
  }, []);
  useUnmount(() => {
    disconnectClient();
  });
  const mqttContextValue = useMemo<MqttContextType>(
    () => ({
      client,
      connectClient: connectClientIfRequired,
      disconnectClient,
      publish: ((...publishArgs: Parameters<MqttClient['publish']>) => {
        const activeClient = clientRef.current;

        if (activeClient?.connected) {
          return activeClient.publish(...publishArgs);
        }

        pendingPublishRef.current.push(publishArgs);
        void connectClientIfRequired();
      }) as MqttClient['publish'],
      unsubscribeFromTopic: (topic: string | string[], callback?: TopicMessageCallback) => {
        try {
          const activeClient = clientRef.current;

          getTopics(topic).forEach((topicName) => {
            if (!removeTopicSubscriber(topicSubscribersRef.current, topicName, callback)) {
              return;
            }

            if (!activeClient?.connected) {
              return;
            }

            activeClient.unsubscribe(topicName, (error) => {
              if (error) {
                console.debug(`mqtt client: Error unsubscribing from ${topicName}:`, error);
              } else {
                console.debug(`mqtt client: Unsubscribed from ${topicName}:`, error);
              }
            });
          });
        } catch (error) {
          console.debug('mqtt client: Error unsubscribe from MQTT topic', error);
        }
      },
      subscribeToTopic: (topic: string | string[], callback: TopicMessageCallback) => {
        try {
          const activeClient = clientRef.current;

          getTopics(topic).forEach((topicName) => {
            if (!addTopicSubscriber(topicSubscribersRef.current, topicName, callback)) {
              return;
            }

            if (!activeClient?.connected) {
              void connectClientIfRequired();
              return;
            }

            activeClient.subscribe(topicName, (error) => {
              if (error) {
                console.debug(`mqtt client: Error subscribing to ${topicName}:`, error);
              } else {
                console.debug(`mqtt client: Subscribed to ${topicName}:`, error);
              }
            });
          });
        } catch (error) {
          console.debug('mqtt client: Error subscribing to MQTT topic', error);
        }
      },
    }),
    [client, connectClientIfRequired, disconnectClient],
  );

  return <MqttContext.Provider value={mqttContextValue}>{children}</MqttContext.Provider>;
}
